import { useState, useCallback } from 'react';

/**
 * Custom hook for managing product search state and functions
 * @param {Object} filterState - Current filter state
 * @param {Object} filterActions - Filter action functions
 * @returns {Object} Product search state and functions
 */
const useProductSearch = (filterState, filterActions) => {
  const [productSearchState, setProductSearchState] = useState({
    query: filterState.searchQuery,
    configure: {
      facetFilters: '',
      facets: '*',
    },
    hitsPerPage: 24,
    page: 1,
  });

  const onSearchUpdate = useCallback(
    searchState => {
      setProductSearchState(searchState);
      filterActions.setSearchQuery(searchState.query || '');
    },
    [filterActions]
  );

  const resetProductSearch = useCallback(() => {
    setProductSearchState(prevState => ({
      ...prevState,
      query: '',
    }));
    filterActions.setSearchQuery('');
    filterActions.setSelectedProduct(null);
  }, [filterActions]);

  const handleProductClicked = useCallback(
    (productId, productName) => {
      if (productId && productName) {
        const newProduct = { id: productId, name: productName };
        filterActions.setSelectedProduct(newProduct);
        filterActions.setSearchQuery(productName);
      } else {
        filterActions.setSelectedProduct(null);
        filterActions.setSearchQuery('');
      }
    },
    [filterActions]
  );

  const handleMaterialOptionChange = useCallback(
    materialOption => {
      if (materialOption) {
        // Toggle search material filter: if same material is selected, clear it; otherwise set it
        const newValue =
          filterState.searchMaterialFilter === materialOption
            ? ''
            : materialOption;
        filterActions.setSearchMaterialFilter(newValue);
        filterActions.setSearchQuery(newValue);
      } else {
        filterActions.setSearchMaterialFilter('');
        filterActions.setSearchQuery('');
      }
    },
    [filterActions, filterState.searchMaterialFilter]
  );

  return {
    productSearchState,
    onSearchUpdate,
    resetProductSearch,
    handleProductClicked,
    handleMaterialOptionChange,
  };
};

export default useProductSearch;
